/**
 * =============================================================================
 * IDS CRM Web - Opportunity Line Item Form (details-only, system rework R1)
 * =============================================================================
 * EDIT-ONLY details editor for a line item: title, expected close,
 * probability, notes. Creation happens on the grouped Create Opportunity page
 * (/pipeline/new). Pricing is deliberately NOT editable here (DL-4): every
 * price change flows through the Opportunity page's audited pricing editor
 * (deviation detection + approval), or a Contract Variation once the line is
 * won. Product and customer are fixed at creation.
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { formatMoney } from '../utils/money';
import './OpportunityForm.css';
import './CommercialLayout.css';

function OpportunityForm() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const { id } = useParams();
  const navigate = useNavigate();
  const backToList = useBackToList('/pipeline');
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const isEdit = Boolean(id);
  const initialCustomerId = searchParams.get('customer_id') || searchParams.get('customer') || '';

  // Sales rework Phase 3: this form is EDIT-ONLY. Creation happens on the
  // grouped Create Opportunity page (/pipeline/new -> OpportunityCreate).
  useEffect(() => {
    if (!isEdit) {
      navigate(`/pipeline/new${initialCustomerId ? `?customer_id=${initialCustomerId}` : ''}`, { replace: true });
    }
  }, [isEdit, initialCustomerId, navigate]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [staleRecord, setStaleRecord] = useState(false);
  const [opportunity, setOpportunity] = useState(null);

  const [formData, setFormData] = useState({
    updated_at: '',
    title: '',
    expected_close: '',
    probability_pct: '50',
    notes: ''
  });

  useEffect(() => {
    if (!isEdit) return;
    const fetchOpportunity = async () => {
      try {
        const response = await authFetch(`/api/v1/opportunities/${id}`);
        if (!response.ok) throw new Error('Opportunity not found');
        const data = await response.json();
        setOpportunity(data);
        setFormData({
          updated_at: data.updated_at || '',
          title: data.title || '',
          expected_close: data.expected_close ? data.expected_close.split('T')[0] : '',
          probability_pct: String(data.probability_pct ?? 50),
          notes: data.notes || ''
        });
      } catch (err) {
        setError(err.message || 'Failed to load opportunity');
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunity();
  }, [id, isEdit, authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    markDirty();
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setStaleRecord(false);

    const probability = parseInt(formData.probability_pct, 10);
    if (Number.isNaN(probability) || probability < 0 || probability > 100) {
      setError('Probability must be between 0 and 100');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`/api/v1/opportunities/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          updated_at: formData.updated_at || undefined,
          title: formData.title,
          expected_close: formData.expected_close || null,
          probability_pct: probability,
          notes: formData.notes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          setStaleRecord(true);
        }
        throw new Error(errorData.message || 'Failed to save opportunity');
      }

      const data = await response.json();
      navigate(`/pipeline/${data.opportunity_id}`);
    } catch (err) {
      setError(err.message || 'Failed to save opportunity');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-page">Loading...</div>;
  }

  const groupLink = opportunity?.opportunity_group_id
    ? `/pipeline/groups/${opportunity.opportunity_group_id}`
    : null;

  return (
    <div className="customer-form-page opportunity-form-page">
      {guardElement}
      <div className="form-header">
        {id ? (
          <Link to={`/pipeline/${id}`} className="back-link">← Opportunity</Link>
        ) : (
          <BackToList to="/pipeline" className="back-link">← Commercial</BackToList>
        )}
        <h1>Edit Line Item{opportunity?.opp_ref ? ` — ${opportunity.opp_ref}` : ''}</h1>
      </div>

      {error && (
        <div className="form-error">
          {error}
          {staleRecord && (
            <button type="button" className="form-error-link" onClick={handleReload}>
              Reload latest version
            </button>
          )}
        </div>
      )}

      <form className="customer-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>Details</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="title">Title *</label>
              <input id="title" name="title" value={formData.title} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="expected_close">Expected Close</label>
              <input
                id="expected_close" name="expected_close" type="date"
                value={formData.expected_close} onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="probability_pct">Probability (%)</label>
              <input
                id="probability_pct" name="probability_pct" type="number" min="0" max="100"
                value={formData.probability_pct} onChange={handleChange}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="notes">Notes</label>
              <textarea id="notes" name="notes" rows={4} value={formData.notes} onChange={handleChange} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Pricing</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Value</label>
              <input
                value={opportunity ? formatMoney(opportunity.value_minor_units, opportunity.currency) : ''}
                readOnly
                className="locked-field"
              />
              <span className="field-help">
                {opportunity?.won_at
                  ? 'This line item is won — pricing is locked and can only change via an approved contract variation.'
                  : 'Pricing is managed on the Opportunity page (deviation detection and approval apply).'}
                {groupLink && (
                  <>
                    {' '}
                    <Link to={groupLink} className="inline-add-link">Open the Opportunity page</Link>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => (id ? navigate(`/pipeline/${id}`) : backToList())}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OpportunityForm;
